import { PhosphorInfo, PhosphorWarning } from "components/Atoms/PhosphorIcon";
import { PhosphorTrashButton } from "components/Atoms/PhosphorIconButton";
import React from "react";
import { ExclusivityCountResult } from "./utils";
import { ButtonText } from "components/Atoms/Button";
import classNames from "classnames";

interface ExclusivtyCheckResultMessageProps {
  result: ExclusivityCountResult;
  wrapperClassName?: string;
  openExclusivityConflictModal: () => void;
}

export const ExclusivityCheckResultMessage = (props: ExclusivtyCheckResultMessageProps) => {
  const { result, wrapperClassName, openExclusivityConflictModal } = props;

  if (result.exclusivityConflictErrors.length > 0) {
    return <ExclusivityCheckProblemMessage
      wrapperClassName={wrapperClassName}
      problems={result.exclusivityConflictErrors}
    />
  }

  if (result.exclusivityConflictsCount === 0) {
    return <div className={classNames("exclusivity-message-wrapper", wrapperClassName)}>
      <div className="grow1">
        {RESX.Exclusivity.NoConflicts}
      </div>
      <ButtonText
        label={RESX.Buttons.View}
        onClick={openExclusivityConflictModal}
      />
    </div>
  }

  return <div className={classNames("exclusivity-message-wrapper exclusivity-message-warning", wrapperClassName)}>
    <div>
      <PhosphorWarning />
    </div>
    <div className="grow1">
      {`${result.exclusivityConflictsCount} ${RESX.Exclusivity.ExclusivityConflicts}`}
    </div>
    <ButtonText
      label={RESX.Buttons.View}
      onClick={openExclusivityConflictModal}
    />
  </div>
}

export const ExclusivityCheckProblemMessage = (props: {
  wrapperClassName?: string;
  problems: server.dto.ExclusivityCheckProblems[]
}) => {
  if (!props.problems || props.problems.length === 0) return null;

  const problemSet = new Set(props.problems);

  const problemMessages: string[] = [];
  if (problemSet.has("hasNoArtist")) problemMessages.push(RESX.Exclusivity.NoArtistSelected);
  if (problemSet.has("showIsCancelled")) problemMessages.push(RESX.Exclusivity.ShowCancelled);

  if (problemSet.has("hasNoVenue")) {
    problemMessages.push(RESX.Exclusivity.NoVenueSelected);
  }
  else if (problemSet.has("venueHasNoGeoLocation")) {
    problemMessages.push(RESX.Exclusivity.VenueNotFoundOnMap);
  }

  if (problemSet.has("unknown")) problemMessages.push(RESX.GeneralLabels.SomethingUnexpectedHappened);

  return <div className={classNames("exclusivity-message-wrapper exclusivity-message-informal", props.wrapperClassName)}>
    <div>
      <PhosphorInfo />
    </div>
    <div>
      {problemMessages.map(msg => <div key={msg}>
        {msg}
      </div>)}
    </div>
  </div>;
};

interface ExclusivityClauseDetailsProps {
  result: ExclusivityCountResult
  clause: server.dto.ExclusivityClause
  onDelete?: () => void
}

export const ExclusivityClauseDetails = (props: ExclusivityClauseDetailsProps) => {
  // We shouldn't show anything if there are problems with exclusivity check, since we cannot add a clause or
  //  perform any checks in that case
  if (props.result.exclusivityConflictErrors.length > 0) {
    return null;
  }

  if (!props.clause) {
    return <div className="exclusivity-clause-details-wrapper">
      {RESX.Exclusivity.NoExclusivityDefined}
    </div>
  }

  return <div className="exclusivity-clause-details-wrapper">
    <div className="exclusivity-clause-details-grid grow1">
      <div className="text-light">{RESX.DateTime.Period}</div>
      <div>-{props.clause.daysBefore} / +{props.clause.daysAfter} {RESX.DateTime.days}</div>
      <div className="text-light">{RESX.Distance.Radius}</div>
      <div>{`${props.clause.radius} ${props.clause.unitOfDistance === "metric" ? "km" : "mi"}`}</div>
    </div>
    {props.onDelete && <div style={{ marginTop: -4 }}>
      <PhosphorTrashButton
        layout="in-square"
        onClick={props.onDelete}
      />
    </div>}
  </div>
}

export const ExclusivityClauseDetailsForSidePanel = (props: ExclusivityClauseDetailsProps) => {
  // We shouldn't show anything if there are problems with exclusivity check, since we cannot add a clause or
  //  perform any checks in that case
  if (props.result.exclusivityConflictErrors.length > 0) {
    return null;
  }

  if (!props.clause) {
    return <div>
      {RESX.Exclusivity.NoExclusivityDefined}
    </div>
  }

  return <div>
    {`${props.clause.radius} ${props.clause.unitOfDistance === "metric"
      ? "km"
      : "mi"}`} radius, -{props.clause.daysBefore} / +{props.clause.daysAfter} {RESX.DateTime.days}
  </div>
}

export const NoConflictsMessage = ({ children }: React.PropsWithChildren<unknown>) => <div className="exclusivity-message-wrapper">
  <div className="grow1">
    {children}
  </div>
</div>